// ============================================================
// aiOptimize — AI最適化（予約オーバーレイの再配置プランナー）
//
// 同一車種グループ内で、予約の貸出・返却日時は変えずに割当車両
// (vehicle_id) だけを再配置し、空きロスが減る配置案を作る。
// 提案はそのまま保存せず、ステージングモード（useStaging）に積んで
// ユーザーが確認 → 「適用」で反映する前提。
//
// 2つのプランナー:
//   - buildOptimizationPlan()       … 既定ルールの決定的プランナー（同期・ネットワーク不要）
//   - buildOptimizationPlanWithAI() … ユーザーの自由指示を Gemini が解釈して
//                                     プランを生成（フロントで重複バリデーション）
//
// 既定の最適化ルール:
//   1. 車両タイプごとに独立して最適化する
//   2. 基本は「左詰め」— 表示順（優先度順）で左の車両から埋める
//   3. ガチャ予約 (car_type === 'Gatcha') はエリアの左側にできるだけ集める
//   4. 高級ガチャ (car_type === 'GatchaH') は右側に集める
//   5. それ以外（車種指定の予約）が残った隙間を左から埋める
//   ※ ガチャが存在しないタイプ（デリカ以外）は 2 の左詰めのみが効く
//
// コンテキストウィンドウ:
//   windowStart〜windowEnd に「貸出開始する」未来の予約だけを移動対象とし、
//   期間外・進行中 (in_use)・完了 (done) の予約は固定ブロックとして
//   重複判定に参加させる（動かさない）。
// ============================================================

import { Reservation, Vehicle } from './types';
import { postAgentChat } from '../../agent/api';

/** 配車を専有するステータス（これ以外 = キャンセル等は重複判定にも参加させない） */
const OCCUPYING_STATUSES = new Set([
  'booked', 'done', 'in_use', 'mail_ready', 'received', 'pending', 'offer', 'request',
]);

/** 移動可能とみなすステータス（進行中 in_use / 完了 done は動かさない） */
const MOVABLE_STATUSES = new Set([
  'booked', 'mail_ready', 'received', 'pending', 'offer', 'request',
]);

export type OptimizeCategory = 'gatcha' | 'gatchaH' | 'other';

export interface OptimizeMove {
  id: string;
  fromVehicleId: string;
  toVehicleId: string;
  category: OptimizeCategory;
}

export interface OptimizeResult {
  moves: OptimizeMove[];
  /** 最適化を実行した車種グループ数 */
  groupCount: number;
  /** 移動対象になった予約の総数（moves はこのうち車両が変わるものだけ） */
  targetCount: number;
}

export interface OptimizeWindowOpts {
  reservations: Reservation[];
  vehicles: Vehicle[];
  /** 最適化対象期間の開始（この日以降に貸出開始する予約が対象） */
  windowStart: Date;
  /** 最適化対象期間の終了（この日までに貸出開始する予約が対象） */
  windowEnd: Date;
}

/** ガチャ判定: OTA メール取込 (m_trigger.php) が car_type に格納する exact 値 */
export function categorizeReservation(r: Reservation): OptimizeCategory {
  const ct = String((r as any).car_type || '').trim().toLowerCase();
  if (ct === 'gatchah') return 'gatchaH';
  // GatchaL（ガチャ低・ファミリータイプ 2026-10-24）は車が通常ガチャと同じなので同じ扱い
  if (ct === 'gatcha' || ct === 'gatchal') return 'gatcha';
  return 'other';
}

interface Interval { s: number; e: number }

function overlapsAny(a: Interval, list: Interval[]): boolean {
  for (const b of list) {
    if (a.s < b.e && b.s < a.e) return true;
  }
  return false;
}

// ------------------------------------------------------------
// 分類（両プランナー共通）
// ------------------------------------------------------------

interface ClassifiedData {
  /** 車種ごとの割当先候補（表示順 = 左→右。out_of_use 除外済み） */
  vehiclesByType: Map<string, Vehicle[]>;
  /** 車種ごとの移動対象予約 */
  movableByType: Map<string, Reservation[]>;
  /** 車両ごとの固定ブロック（コンテキスト範囲に絞り済み） */
  fixedByVehicle: Map<string, Interval[]>;
  vehicleById: Map<string, Vehicle>;
  groupCount: number;
  targetCount: number;
}

function classify(opts: OptimizeWindowOpts): ClassifiedData {
  const { reservations, vehicles, windowStart, windowEnd } = opts;
  const now = Date.now();
  const windowStartMs = windowStart.getTime();
  const windowEndMs = windowEnd.getTime();

  const vehicleById = new Map(vehicles.map(v => [String(v.id), v]));

  // 車種ごとの割当先候補（表示順を維持。out_of_use は割当先から除外）
  const vehiclesByType = new Map<string, Vehicle[]>();
  for (const v of vehicles) {
    const t = String(v.car_type_id ?? '');
    if (!t) continue;
    if ((v as any).out_of_use) continue;
    const arr = vehiclesByType.get(t) || [];
    arr.push(v);
    vehiclesByType.set(t, arr);
  }

  // 予約を「移動可能」と「固定ブロック」に分類
  const movableByType = new Map<string, Reservation[]>();
  const fixedByVehicle = new Map<string, Interval[]>();
  for (const r of reservations) {
    if (!OCCUPYING_STATUSES.has(String(r.status))) continue; // キャンセル等は無視
    const s = new Date(r.start_date).getTime();
    const e = new Date(r.end_date).getTime();
    if (!isFinite(s) || !isFinite(e) || e <= s) continue;
    if (e <= now) continue; // 完全に過去 → 今後の配置に無関係
    if (String(r.id).startsWith('tmp_')) continue;

    const vid = String(r.vehicle_id);
    const veh = vehicleById.get(vid);
    const typeId = veh ? String(veh.car_type_id ?? '') : '';
    const candidates = typeId ? vehiclesByType.get(typeId) : undefined;

    // コンテキストウィンドウ: 指定期間内に貸出開始する未来の予約だけを動かす
    const movable =
      !!veh &&
      !!candidates && candidates.length >= 2 &&
      !(veh as any).out_of_use &&
      MOVABLE_STATUSES.has(String(r.status)) &&
      s > now && s >= windowStartMs && s <= windowEndMs;

    if (movable) {
      const arr = movableByType.get(typeId) || [];
      arr.push(r);
      movableByType.set(typeId, arr);
    } else {
      const arr = fixedByVehicle.get(vid) || [];
      arr.push({ s, e });
      fixedByVehicle.set(vid, arr);
    }
  }

  // 固定ブロックをコンテキスト範囲（移動対象と干渉しうる区間）に絞る
  let contextEnd = windowEndMs;
  for (const list of movableByType.values()) {
    for (const r of list) {
      const e = new Date(r.end_date).getTime();
      if (isFinite(e) && e > contextEnd) contextEnd = e;
    }
  }
  const contextStart = Math.max(now, windowStartMs);
  for (const [vid, blocks] of fixedByVehicle) {
    fixedByVehicle.set(vid, blocks.filter(f => f.e > contextStart && f.s < contextEnd));
  }

  let groupCount = 0;
  let targetCount = 0;
  for (const [typeId, groupVehicles] of vehiclesByType) {
    const movables = movableByType.get(typeId) || [];
    if (groupVehicles.length < 2 || movables.length === 0) continue;
    groupCount++;
    targetCount += movables.length;
  }

  return { vehiclesByType, movableByType, fixedByVehicle, vehicleById, groupCount, targetCount };
}

// ------------------------------------------------------------
// 既定ルールの決定的プランナー
// ------------------------------------------------------------

/**
 * 車種グループ 1 つ分のルールベース再配置。
 * @param groupVehicles 表示順（左→右）の車両リスト
 */
function packGroup(
  groupVehicles: Vehicle[],
  movables: Reservation[],
  fixedByVehicle: Map<string, Interval[]>,
): OptimizeMove[] {
  const vehicleIdsLtr = groupVehicles.map(v => String(v.id));
  const vehicleIdsRtl = [...vehicleIdsLtr].reverse();

  // 車両ごとの専有区間（固定ブロックで初期化）
  const occupancy = new Map<string, Interval[]>();
  for (const vid of vehicleIdsLtr) {
    occupancy.set(vid, [...(fixedByVehicle.get(vid) || [])]);
  }

  // カテゴリ分類 + 貸出開始の昇順ソート
  const byStart = (a: Reservation, b: Reservation) =>
    new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
  const gatcha = movables.filter(r => categorizeReservation(r) === 'gatcha').sort(byStart);
  const gatchaH = movables.filter(r => categorizeReservation(r) === 'gatchaH').sort(byStart);
  const others = movables.filter(r => categorizeReservation(r) === 'other').sort(byStart);

  const moves: OptimizeMove[] = [];

  const place = (r: Reservation, order: string[], category: OptimizeCategory) => {
    const iv: Interval = {
      s: new Date(r.start_date).getTime(),
      e: new Date(r.end_date).getTime(),
    };
    const from = String(r.vehicle_id);
    for (const vid of order) {
      if (overlapsAny(iv, occupancy.get(vid)!)) continue;
      occupancy.get(vid)!.push(iv);
      if (vid !== from) {
        moves.push({ id: String(r.id), fromVehicleId: from, toVehicleId: vid, category });
      }
      return;
    }
    // どこにも置けない（既存データの重複等）→ 現在の車両に留める
    const arr = occupancy.get(from);
    if (arr) arr.push(iv);
  };

  // 1. ガチャ → 左から / 2. 高級ガチャ → 右から / 3. 指定 → 左から隙間埋め
  for (const r of gatcha) place(r, vehicleIdsLtr, 'gatcha');
  for (const r of gatchaH) place(r, vehicleIdsRtl, 'gatchaH');
  for (const r of others) place(r, vehicleIdsLtr, 'other');

  return moves;
}

/**
 * 既定ルールで全車種グループの最適化プランを作る（決定的・同期）。
 * reservations にはステージ値マージ済みの配列（displayReservations）を渡すと
 * 「今画面に見えている配置」を起点に計画される。
 * vehicles は表示順（左→右）で渡すこと（filteredVehicles がそのまま使える）。
 */
export function buildOptimizationPlan(opts: OptimizeWindowOpts): OptimizeResult {
  const data = classify(opts);
  const moves: OptimizeMove[] = [];
  for (const [typeId, groupVehicles] of data.vehiclesByType) {
    const movables = data.movableByType.get(typeId) || [];
    if (groupVehicles.length < 2 || movables.length === 0) continue;
    moves.push(...packGroup(groupVehicles, movables, data.fixedByVehicle));
  }
  return { moves, groupCount: data.groupCount, targetCount: data.targetCount };
}

// ------------------------------------------------------------
// 指示つきプランナー（Gemini が指示を解釈 → フロントで検証）
// ------------------------------------------------------------

function stripCodeFence(text: string): string {
  return text
    .replace(/^\s*```(?:json)?\s*/i, '')
    .replace(/\s*```\s*$/i, '')
    .trim();
}

/**
 * Gemini の提案 moves を検証して安全な moves に変換する。
 * - グループ外の予約 / 車両・タイプ跨ぎの移動は無視
 * - 「新規の時間重複」を生む移動は元の車両に差し戻す
 *   （既存データにもともとある重複はベースラインとして許容）
 */
function validateProposal(
  data: ClassifiedData,
  proposal: Map<string, string>,
): OptimizeMove[] {
  // 全移動対象の元割当・区間・タイプ
  const orig = new Map<string, string>();
  const intervalOf = new Map<string, Interval>();
  const typeOf = new Map<string, string>();
  const reservationOf = new Map<string, Reservation>();
  for (const [typeId, list] of data.movableByType) {
    for (const r of list) {
      const id = String(r.id);
      orig.set(id, String(r.vehicle_id));
      intervalOf.set(id, { s: new Date(r.start_date).getTime(), e: new Date(r.end_date).getTime() });
      typeOf.set(id, typeId);
      reservationOf.set(id, r);
    }
  }

  // 提案の正規化: 既知の予約 → 同一タイプの候補車両のみ
  const assign = new Map(orig);
  for (const [id, to] of proposal) {
    if (!orig.has(id)) continue;
    const typeId = typeOf.get(id)!;
    const candidates = data.vehiclesByType.get(typeId) || [];
    if (!candidates.some(v => String(v.id) === to)) continue;
    assign.set(id, to);
  }

  const buildOverlapPairs = (a: Map<string, string>): Set<string> => {
    const byVehicle = new Map<string, { id: string; s: number; e: number }[]>();
    for (const [id, vid] of a) {
      const iv = intervalOf.get(id)!;
      const arr = byVehicle.get(vid) || [];
      arr.push({ id, s: iv.s, e: iv.e });
      byVehicle.set(vid, arr);
    }
    for (const [vid, arr] of byVehicle) {
      const fixed = data.fixedByVehicle.get(vid) || [];
      fixed.forEach((f, i) => arr.push({ id: `__fixed_${vid}_${i}`, s: f.s, e: f.e }));
    }
    const pairs = new Set<string>();
    for (const arr of byVehicle.values()) {
      arr.sort((x, y) => x.s - y.s);
      for (let i = 0; i < arr.length; i++) {
        for (let j = i + 1; j < arr.length && arr[j].s < arr[i].e; j++) {
          pairs.add([arr[i].id, arr[j].id].sort().join('|'));
        }
      }
    }
    return pairs;
  };

  const baseline = buildOverlapPairs(orig);
  for (let iter = 0; iter < 20; iter++) {
    const fresh = Array.from(buildOverlapPairs(assign)).filter(k => !baseline.has(k));
    if (fresh.length === 0) break;
    let reverted = false;
    for (const key of fresh) {
      for (const id of key.split('|')) {
        if (orig.has(id) && assign.get(id) !== orig.get(id)) {
          assign.set(id, orig.get(id)!);
          reverted = true;
        }
      }
    }
    if (!reverted) break; // 提案由来でない重複 → これ以上は解消できない
  }

  const moves: OptimizeMove[] = [];
  for (const [id, vid] of assign) {
    const from = orig.get(id)!;
    if (vid === from) continue;
    moves.push({
      id,
      fromVehicleId: from,
      toVehicleId: vid,
      category: categorizeReservation(reservationOf.get(id)!),
    });
  }
  return moves;
}

/**
 * ユーザーの自由指示を Gemini に解釈させてプランを作る。
 * 既定ルールをベースに指示を最優先で反映する。提案はフロントで
 * validateProposal() を必ず通す（タイプ跨ぎ・新規重複は無効化）。
 */
export async function buildOptimizationPlanWithAI(
  opts: OptimizeWindowOpts & { instruction: string },
): Promise<OptimizeResult> {
  const data = classify(opts);
  if (data.groupCount === 0) {
    return { moves: [], groupCount: 0, targetCount: 0 };
  }

  // グループ横断の 1 リクエストにまとめる（指示が特定タイプに言及することがあるため）
  const groups: any[] = [];
  for (const [typeId, groupVehicles] of data.vehiclesByType) {
    const movables = data.movableByType.get(typeId) || [];
    if (groupVehicles.length < 2 || movables.length === 0) continue;
    groups.push({
      type_id: typeId,
      type_name: groupVehicles[0]?.vehicle_type?.name || groupVehicles[0]?.car_type || typeId,
      // 左→右の表示順
      vehicles: groupVehicles.map(v => ({ id: String(v.id), code: v.vehicle_code || String(v.id) })),
      reservations: movables.map(r => ({
        id: String(r.id),
        vehicle_id: String(r.vehicle_id),
        start: r.start_date,
        end: r.end_date,
        category: categorizeReservation(r),
        customer: r.customer_name || '',
      })),
      fixed: groupVehicles.flatMap(v =>
        (data.fixedByVehicle.get(String(v.id)) || []).map(f => ({
          vehicle_id: String(v.id),
          start: new Date(f.s).toISOString(),
          end: new Date(f.e).toISOString(),
        })),
      ),
    });
  }

  const systemInstruction = [
    'あなたはレンタカー配車表の再配置プランナーです。',
    '車種グループごとに、予約の貸出・返却日時は一切変えずに割当車両 (vehicle_id) だけを再配置します。',
    '',
    '## 既定ルール（指示が無い部分はこれに従う）',
    '1. 車種グループを跨ぐ移動は禁止',
    '2. 基本は左詰め（各グループの vehicles 配列は左→右の並び。先頭側 = 左）',
    "3. category === 'gatcha' の予約は左側の車両に集める",
    "4. category === 'gatchaH' の予約は右側の車両に集める",
    "5. category === 'other'（車種指定）は残った隙間を左から埋める",
    '',
    '## ユーザー指示（最優先。既定ルールと矛盾する場合は指示に従う）',
    '"""',
    opts.instruction,
    '"""',
    '',
    '## 厳守事項',
    '- 予約の日時は絶対に変更しない（車両の付け替えのみ）',
    '- vehicle_id は同じグループの vehicles にある ID のみ使用する',
    '- 同じ車両の予約同士（fixed 含む）が時間重複する配置は禁止',
    '- 車両を変更する予約だけを moves に含める（変更なしは含めない）',
    '',
    '## 出力形式（この JSON のみを返す。前置き・説明・コードブロック禁止）',
    '{"moves":[{"id":"<予約ID>","vehicle_id":"<移動先車両ID>"}]}',
    '移動が不要な場合は {"moves":[]} を返す。',
  ].join('\n');

  const res = await postAgentChat({
    system_instruction: systemInstruction,
    contents: [{ role: 'user', parts: [{ text: JSON.stringify({ groups }) }] }],
    tools: [],
  });
  const raw = res.content?.parts?.find((p: any) => typeof p.text === 'string')?.text;
  if (!raw) throw new Error('AIから空のレスポンスが返りました');
  const parsed = JSON.parse(stripCodeFence(raw));
  const rawMoves: any[] = Array.isArray(parsed?.moves) ? parsed.moves : [];

  const proposal = new Map<string, string>();
  for (const m of rawMoves) {
    const id = String(m?.id ?? '');
    const to = String(m?.vehicle_id ?? '');
    if (id && to) proposal.set(id, to);
  }

  const moves = validateProposal(data, proposal);
  return { moves, groupCount: data.groupCount, targetCount: data.targetCount };
}
